import { METRICS, SOCIAL_PLATFORMS } from '~/constants';
import { TMediaPackPosition } from '~/types';
import { formatAbbr, formatPercentage } from '~/utils';

import {
  IMetricPlatforms,
  IMetricsConfig,
} from './MediaPackDetailEditHighlightsModal.types';

export const PLATFORM_METRICS: IMetricPlatforms = {
  [SOCIAL_PLATFORMS.instagram]: [
    {
      description: null,
      key: 'igFollowersCount',
      title: 'Total Followers',
      transformValue: formatAbbr,
    },
    {
      description: 'Avg number of likes, comments, and saves.',
      key: 'igAvgFeedReelsEngagements',
      title: 'Average Engagements',
      transformValue: formatAbbr,
    },
    {
      description:
        'Avg percentage of unique accounts who have seen and engaged with the posts.',
      key: 'igAvgFeedReelsReachEngRate',
      title: 'Reach Engagement Rate',
      transformValue: (value: number, defaultValue?: string) =>
        formatPercentage(value, defaultValue, 1),
    },
    {
      description:
        'Avg percentage of followers who have engaged with the posts.',
      key: 'igAvgFeedReelsFollowerEngRate',
      title: 'Follower Engagement Rate',
      transformValue: (value: number, defaultValue?: string) =>
        formatPercentage(value, defaultValue, 1),
    },
    {
      description:
        'Avg number of unique accounts that have seen the posts and stories.',
      key: 'igAvgFeedReelsStoryReach',
      title: 'Average Reach',
      transformValue: formatAbbr,
    },
    {
      description:
        "Avg number of times the creator's posts and stories have been seen.",
      key: 'igAvgFeedStoryImpressions',
      title: 'Average Impressions',
      transformValue: formatAbbr,
    },
    {
      description:
        "Avg number of unique accounts that have seen the creator's stories.",
      key: 'igAvgStoryReach',
      title: 'Average Story Reach',
      transformValue: formatAbbr,
    },
    {
      description: "Avg number of times the creator's stories have been seen.",
      key: 'igAvgStoryImpressions',
      title: 'Average Story Impressions',
      transformValue: formatAbbr,
    },
    {
      description: 'Avg number of video sessions on the reels.',
      key: 'igAvgReelsViews',
      title: 'Average Reels Views',
      transformValue: formatAbbr,
    },
    {
      description:
        'Avg percentage of engagements to total plays (views) of the reels.',
      key: 'igAvgReelsViewsEngRate',
      title: 'View Engagement Rate',
      transformValue: (value: number, defaultValue?: string) =>
        formatPercentage(value, defaultValue, 1),
    },
  ],
  [SOCIAL_PLATFORMS.tiktok]: [
    {
      description: null,
      key: 'ttFollowersCount',
      title: 'Total Followers',
      transformValue: formatAbbr,
    },
    {
      description:
        "Avg number of times each of the creator's videos has been viewed.",
      key: 'ttAvgViews',
      title: 'Average Views',
      transformValue: formatAbbr,
    },
    {
      description: 'Avg number of likes, comments and shares.',
      key: 'ttAvgEngagements',
      title: 'Average Engagements',
      transformValue: formatAbbr,
    },
    {
      description: null,
      key: 'ttAvgLikes',
      title: 'Average Likes',
      transformValue: formatAbbr,
    },
    {
      description: null,
      key: 'ttAvgComments',
      title: 'Average Comments',
      transformValue: formatAbbr,
    },
    {
      description: null,
      key: 'ttAvgShares',
      title: 'Average Shares',
      transformValue: formatAbbr,
    },
    {
      description: 'Avg engagements divided by the avg video views.',
      key: 'ttAvgViewsEngRate',
      title: 'View Engagement Rate',
      transformValue: (value: number, defaultValue?: string) =>
        formatPercentage(value, defaultValue, 1),
    },
  ],
  [SOCIAL_PLATFORMS.youtube]: [
    {
      description: null,
      key: 'ytFollowersCount',
      title: 'Total Subscribers',
      transformValue: formatAbbr,
    },
    {
      description:
        "Avg number of times each of the creator's videos has been viewed.",
      key: 'ytAvgViews',
      title: 'Average Views',
      transformValue: formatAbbr,
    },
    {
      description: null,
      key: 'ytAvgLikes',
      title: 'Average Likes',
      transformValue: formatAbbr,
    },
    {
      description: null,
      key: 'ytAvgComments',
      title: 'Average Comments',
      transformValue: formatAbbr,
    },
    {
      description: null,
      key: 'ytAvgViewRate',
      title: 'Average Views Rate',
      transformValue: (value: number, defaultValue?: string) =>
        formatPercentage(value, defaultValue, 1),
    },
  ],
};

export const METRIC_CONFIG: IMetricsConfig = {
  [SOCIAL_PLATFORMS.instagram]: {
    [METRICS.metric1]: {
      defaultMetricKey: 'igFollowersCount',
      isMultiselect: false,
    },
    [METRICS.metric2]: {
      defaultMetricKey: 'igAvgFeedReelsReachEngRate',
      isMultiselect: false,
    },
    [METRICS.metric3]: {
      defaultMetricKey: ['igAvgFeedReelsStoryReach', 'igAvgStoryReach'],
      isMultiselect: true,
      minMetricCount: 2,
      maxMetricCount: 2,
      title: 'Highlights',
    },
    lastUpdatedKey: 'igLastUpdatedAt',
  },
  [SOCIAL_PLATFORMS.tiktok]: {
    [METRICS.metric1]: {
      defaultMetricKey: 'ttFollowersCount',
      isMultiselect: false,
    },
    [METRICS.metric2]: {
      defaultMetricKey: 'ttAvgViews',
      isMultiselect: false,
    },
    [METRICS.metric3]: {
      defaultMetricKey: ['ttAvgViewsEngRate', 'ttAvgEngagements'],
      isMultiselect: true,
      minMetricCount: 2,
      maxMetricCount: 2,
      title: 'Highlights',
    },
    lastUpdatedKey: 'ttLastUpdatedAt',
  },
  [SOCIAL_PLATFORMS.youtube]: {
    [METRICS.metric1]: {
      defaultMetricKey: 'ytFollowersCount',
      isMultiselect: false,
    },
    [METRICS.metric2]: {
      defaultMetricKey: 'ytAvgViews',
      isMultiselect: false,
    },
    [METRICS.metric3]: {
      defaultMetricKey: ['ytAvgLikes', 'ytAvgComments'],
      isMultiselect: true,
      minMetricCount: 2,
      maxMetricCount: 2,
      title: 'Highlights',
    },
    lastUpdatedKey: 'ytLastUpdatedAt',
  },
};

export const METRIC_POSITION_DEFAULT: TMediaPackPosition = {
  [SOCIAL_PLATFORMS.instagram]: {
    [METRICS.metric1]: [],
    [METRICS.metric2]: [],
    [METRICS.metric3]: [],
  },
  [SOCIAL_PLATFORMS.tiktok]: {
    [METRICS.metric1]: [],
    [METRICS.metric2]: [],
    [METRICS.metric3]: [],
  },
  [SOCIAL_PLATFORMS.youtube]: {
    [METRICS.metric1]: [],
    [METRICS.metric2]: [],
    [METRICS.metric3]: [],
  },
};
